import { CommonLocale, DetailsReportLocale } from "@/locales/localeid";
import { LanguageSkillModel, VCSLevelModel } from "@/models/title-list-model";
import TitleListState from "@/modules/title-list";
import { AllLanguageSkill } from "@/util/constants";
import { fmtMsg, MessageHelper, NotificationType } from "commonui";
import { Component, Ref, Vue, Watch } from "vue-property-decorator";
import { getModule } from "vuex-module-decorators";
import { Alert, Select } from "ant-design-vue";
import { GridRef } from "@/components/wijmo-grid/grid/grid";
import Grid from "@/components/wijmo-grid/grid/grid.vue";
import { assign, isNull, isUndefined } from "lodash";
import { DetailsReportTagListModel, TagAssociationDetails } from "@/models/details-report-model";
import { DetailsReportService } from "@/services/details-report.service";
import { CellType, FlexGrid as IFlexGrid } from "wijmo/wijmo.grid";
import { VersionGroupModule } from "@/modules/versiongroup";
import {
	getEndLevel,
	getSelectedLevels,
	getSkillSelected,
	isAnyVersionSelected,
	sortLevelByLicenseAndUnit,
	sortTagList,
	insertSequence,
	getRouteAccessLevel,
	disabledDueToAccessLevel,
	fetchLevels
} from "@/util/func";
import { IWorkbookCell } from "wijmo/wijmo.xlsx";
import PageTitle from "@/components/page-title/page-title.vue";
import { AccessLevel } from "@/util/enum";

interface RequiredParams {
	lsStartLevel: number;
	lsEndLevel: number;
	gsStartLevel: number;
	gsEndLevel: number;
	languageSkills: number[];
	codes: string[];
	gsVersionGroupId: string | null;
	lsVersionGroupId: string | null;
}

@Component({
	components: {
		Select,
		Option: Select.Option,
		Grid,
		Alert,
		PageTitle
	}
})
export default class DetailsReport extends Vue {
	@Ref() readonly gridCompRef!: GridRef;
	public readonly allLanguageSkill = AllLanguageSkill;
	public titleListModule = getModule(TitleListState);
	public startLevelSelected: number | null = null;
	public endLevelSelected: number | null = null;
	public skillSelected: number[] = [];
	public units: VCSLevelModel[] = [];
	public startLevel: VCSLevelModel[] = [];
	public endLevel: VCSLevelModel[] = [];
	public tagList: DetailsReportTagListModel[] = [];
	public gridLoading: boolean = false;
	public expandedRows: number[] = [];
	private versionGroupModule = VersionGroupModule; // For watch need to create local reference
	public noVersion: boolean = false;
	public commonLocale = CommonLocale;
	public accessLevel = AccessLevel;

	get languageSkills(): LanguageSkillModel[] {
		return this.titleListModule.languageSkills;
	}
	get currentRouteAccessLevel() {
		return getRouteAccessLevel(this.$route);
	}

	get disabledDueToAccessLevel() {
		return disabledDueToAccessLevel(this.currentRouteAccessLevel);
	}

	created() {
		if (!isAnyVersionSelected()) {
			this.noVersion = true;
		}
		this.units = fetchLevels(this.titleListModule.levels, this.versionGroupModule.selectedLsVersion, this.versionGroupModule.selectedGsVersion)
		this.skillSelected = [this.allLanguageSkill];
		this.selectStartAndEndLevel();
	}

	mounted() {
		this.versionGroupModule.setDisabled(false);
	}

	destroyed() {
		this.versionGroupModule.setDisabled(true);
	}

	@Watch("versionGroupModule.selectedGsVersion")
	@Watch("versionGroupModule.selectedLsVersion")
	isVersionUpdated() {
		if (isAnyVersionSelected()) {
			this.noVersion = false;
			this.units = fetchLevels(this.titleListModule.levels, this.versionGroupModule.selectedLsVersion, this.versionGroupModule.selectedGsVersion)
			this.selectStartAndEndLevel();
		} else {
			this.noVersion = true;
		}
	}

	getTagDetails() {
		this.setGridLoading(true);

		if (this.noVersion) {
			return;
		}
		const params = this.getRequiredParams();
		if (!isNull(params)) {
			DetailsReportService.getContentTagList(params)
				.then((data: DetailsReportTagListModel[]) => {
					this.expandedRows = [];

					const updatedTags = data.map((t) =>
						t.contentTextCount > 0 ? assign(t, { children: [{ unitDisplayName: null }] }) : t
					);

					let newTagList = sortTagList(updatedTags, "code");
					newTagList = insertSequence(newTagList);
					this.tagList = newTagList;
					setTimeout(() => {
						this.gridCompRef.collapseRows();
						this.setGridLoading(false);
					}, 3000);
				})
				.catch((ex) => {
					this.setGridLoading(false);
				});
		}
	}

	loadedTagRows(s: any, e: any) {
		for (var j = 0; j < s.rows.length; j++) {
			if (s.rows[j].hasChildren && !this.expandedRows.includes(s.rows[j].dataItem.id)) {
				s.rows[j].isCollapsed = true;
			}
		}
	}

	getRequiredParams(tagCodes: string[] = []): RequiredParams | null {
		if (!isNull(this.startLevelSelected) && !isNull(this.endLevelSelected) && isAnyVersionSelected()) {
			const selectedLevels = getSelectedLevels(
				this.titleListModule.levels,
				this.startLevelSelected,
				this.endLevelSelected
			);
			const skills = this.skillSelected.includes(this.allLanguageSkill)
				? this.languageSkills.map((l) => l.id)
				: this.skillSelected;
			return {
				gsVersionGroupId:
					VersionGroupModule.selectedGsVersion !== "-1" ? VersionGroupModule.selectedGsVersion : null,
				lsVersionGroupId:
					VersionGroupModule.selectedLsVersion !== "-1" ? VersionGroupModule.selectedLsVersion : null,
				lsStartLevel: selectedLevels.lsStartUnit,
				lsEndLevel: selectedLevels.lsEndUnit,
				gsStartLevel: selectedLevels.gsStartUnit,
				gsEndLevel: selectedLevels.gsEndUnit,
				codes: tagCodes,
				languageSkills: skills
			};
		} else {
			MessageHelper.Message(
				NotificationType.Warning,
				this.localizedString(this.detailsReportLocale.DetailsReportInvalidParamsError)
			);
			return null;
		}
	}

	setTreeItemSourceForExport(s: any) {
		return new Promise((resolve, reject) => {
			const codes: string[] = [];
			for (var j = 0; j < s.rows.length; j++) {
				if (
					s.rows[j].hasChildren &&
					s.rows[j].dataItem.children.length > 0 &&
					isNull(s.rows[j].dataItem.children[0].unitDisplayName)
				) {
					codes.push(s.rows[j].dataItem.code);
				}
			}

			if (codes.length === 0) {
				resolve();
				return;
			}

			const params = this.getRequiredParams(codes);
			if (!isNull(params)) {
				this.setGridLoading(true);
				DetailsReportService.getContentTextReport(params)
					.then((data: TagAssociationDetails[]) => {
						for (var j = 0; j < s.rows.length; j++) {
							if (
								s.rows[j].hasChildren &&
								s.rows[j].dataItem.children.length > 0 &&
								isNull(s.rows[j].dataItem.children[0].unitDisplayName)
							) {
								const childData = data.filter(
									(t) => t.codeInfo.trim() === s.rows[j].dataItem.code.trim()
								);
								if (childData.length > 0) {
									s.rows[j].dataItem.children = childData;
								}
							}
						}
						// refresh the view
						s.collectionView.refresh();
						this.setGridLoading(false);
						resolve();
					})
					.catch((ex) => {
						this.setGridLoading(false);
						reject();
					});
			} else {
				resolve();
			}
		});
	}

	onGroupCollapsedChanged(s: IFlexGrid & any, e: any) {
		const row: any = s.rows[e.row];
		const item = row.dataItem;
		// did we just expand a node with a dummy child?
		if (
			row.level === 0 &&
			!row.isCollapsed &&
			item.children.length == 1 &&
			isNull(item.children[0].unitDisplayName)
		) {
			// can't lazy load while updating rows
			if (s.rows.isUpdating) {
				row.isCollapsed = true;
				return;
			}

			if (!isUndefined(item.code) && !isNull(item.code)) {
				const params = this.getRequiredParams([item.code]);
				if (!isNull(params)) {
					this.setGridLoading(true);
					DetailsReportService.getContentTextReport(params)
						.then((data: TagAssociationDetails[]) => {
							if (data.length > 0) {
								// replace the dummy child with actual nodes
								item.children.length = 0;
								item.children = data;
								this.expandedRows.push(item.id);
								// refresh the view
								const currentPage = s.collectionView.pageIndex;
								s.collectionView.refresh();
								setTimeout(() => {
									s.collectionView.moveToPage(currentPage);
									this.gridCompRef.setCurrentPage(currentPage + 1);
									setTimeout(() => this.setGridLoading(false), 50); // to make fluctuation less visible
								}, 200);
							}
						})
						.catch((ex) => {
							this.setGridLoading(false);
						});
				}
			}
		}

		if (row.level === 0 && row.isCollapsed && !isNull(item.children[0].unitDisplayName)) {
			this.expandedRows = this.expandedRows.filter((e) => e !== item.id);
		} else if (
			row.level === 0 &&
			!row.isCollapsed &&
			!isNull(item.children[0].unitDisplayName) &&
			!this.expandedRows.includes(item.id)
		) {
			this.expandedRows.push(item.id);
		}
	}

	setGridLoading(value: boolean) {
		this.gridLoading = value;
	}

	get tagDefaultSort() {
		return {
			column: "sequence",
			ascending: true
		};
	}

	isExpanded(id: number) {
		return this.expandedRows.some((e) => e === id);
	}

	setEndLevels(startLevelId: number) {
		this.endLevel = getEndLevel(this.units, startLevelId);

		if (this.endLevel.length > 0) {
			this.endLevelSelected = this.endLevel[0].id;
			this.getTagDetails();
		} else {
			this.endLevel = [];
			this.endLevelSelected = null;
		}
	}

	get detailsReportLocale() {
		return DetailsReportLocale;
	}

	onStartLevelChange(value: number) {
		this.setEndLevels(value);
	}

	onEndLevelChange() {
		this.getTagDetails();
	}

	localizedString(id: string, params?: {}) {
		return fmtMsg(id, params);
	}

	onSkillChange(selectedValues) {
		this.skillSelected = getSkillSelected(selectedValues);
		this.getTagDetails();
	}

	get exportOptions() {
		return {
			exportGroupFormatItem: (args) => {
				const cell: IWorkbookCell = args.xlsxCell;
				if (args.panel.cellType === CellType.ColumnHeader) {
					cell.style!.font = { bold: true };
				} else if (args.panel.cellType === CellType.Cell && args.col === 0) {
					const currentRow = args.getRow();
					if (currentRow?.level === 0) {
						cell.style!.format = "General";
						cell.value = currentRow?.dataItem?.code;
					}
				}
			},
			filename: this.localizedString(this.detailsReportLocale.DetailsReportExportTitle)
		};
	}


	selectStartAndEndLevel() {
		this.startLevel = sortLevelByLicenseAndUnit(this.units);
		if (this.startLevel.length > 0) {
			this.startLevelSelected = this.startLevel[0].id;
			this.setEndLevels(this.startLevelSelected);
		}
	}
}
